import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import {
  DEFAULT_NAV_ITEMS,
  FACILITY_AND_FIELD_USER_TYPES,
  FACILITY_NAV_ITEMS,
  USER_TYPE,
} from './constant';
import { shouldLockLoggedInHierarchyField } from './utility';

export const useUserTypeMeta = (userTypeValue) => {
  return useMemo(() => {
    const isFacilityLevelUser = userTypeValue === USER_TYPE.FACILITY_LEVEL;
    const isFieldLevelUser = userTypeValue === USER_TYPE.FIELD_LEVEL;
    const navItemsModal = FACILITY_AND_FIELD_USER_TYPES.includes(userTypeValue)
      ? FACILITY_NAV_ITEMS
      : DEFAULT_NAV_ITEMS;

    return {
      isFacilityLevelUser,
      isFieldLevelUser,
      navItemsModal,
    };
  }, [userTypeValue]);
};

export const useDebouncedCallback = (callback, delay = 500) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useMemo(() => {
    const fn = _.debounce((...args) => {
      callbackRef.current?.(...args);
    }, delay);
    return fn;
  }, [delay]);

  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
};

export const useHierarchyCascadeEffects = (params) => {
  const latestRef = useRef(params);
  const previousZoneIdRef = useRef('');
  const {
    roleFormState,
    roleFormData,
    hasZonesCreated,
    userTypeName,
    hasBlocksForDistrict,
    loggedInUserDetails,
  } = params;

  const getSingleSelectedId = (selectedId) => {
    if (Array.isArray(selectedId)) {
      return selectedId.length === 1 ? selectedId[0] : '';
    }

    return typeof selectedId === 'string' ? selectedId : '';
  };

  useEffect(() => {
    latestRef.current = params;
  }, [params]);

  useEffect(() => {
    const current = latestRef.current;
    const selectedZoneId = getSingleSelectedId(current.roleFormState?.zone_div_circle?.id);
    const didClearZoneSelection = !!previousZoneIdRef.current && !selectedZoneId;

    if (
      !!selectedZoneId &&
      current.roleFormData.zone_div_circle['#multiDropdown'] === false &&
      current.hasZonesCreated
    ) {
      current.getDistrictForZDC(selectedZoneId);
    } else {
      current.getDistrictForState(
        current.roleFormState?.state?.id,
        didClearZoneSelection ? { forceClearSelection: true, allowAutoSelectSingle: false } : undefined,
      );
    }

    previousZoneIdRef.current = selectedZoneId;

    // Keep District intact when the selected Zone Division/Circle changes.
    // Only an actual District change should reset the downstream hierarchy.
  }, [roleFormState?.zone_div_circle?.id, roleFormState?.state?.id, roleFormState?.district?.id, roleFormData?.zone_div_circle?.['#multiDropdown'], hasZonesCreated, userTypeName]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.roleFormState.district.id) {
      current.getSubDistBlock(current.roleFormState.district.id);
      current.setRoleFormState((roleFormState) => ({
        ...roleFormState,
        sub_district: {
          ...roleFormState.sub_district,
          value: '',
          id: '',
        },
        health_block: {
          ...roleFormState.health_block,
          value: '',
          id: '',
        },
        sub_division: {
          ...roleFormState.health_block,
          value: '',
          id: '',
        },
        block: {
          ...roleFormState.block,
          value: '',
          id: '',
        },
      }));
      return;
    }

    current.setRoleFormState((roleFormState) => ({
      ...roleFormState,
      sub_district: {
        ...roleFormState.sub_district,
        value: '',
        id: '',
      },
      health_block: {
        ...roleFormState.health_block,
        value: '',
        id: '',
      },
      sub_division: {
        ...roleFormState.health_block,
        value: '',
        id: '',
      },
      block: {
        ...roleFormState.block,
        value: '',
        id: '',
      },
    }));
    current.setRoleFormData((roleFormData) => ({
      ...roleFormData,
      sub_district: {
        ...roleFormData.sub_district,
        '#options': [],
      },
      health_block: {
        ...roleFormData.health_block,
        '#options': [],
      },
      sub_division: {
        ...roleFormData.sub_division,
        '#options': [],
      },
      block: {
        ...roleFormData.block,
        '#options': [],
      },
    }));
  }, [roleFormState?.district?.id]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.roleFormState.ulb.id) {
      current.getWard(current.roleFormState.ulb.id);
      return;
    }

    current.setRoleFormState((roleFormState) => ({
      ...roleFormState,
      ward: {
        ...roleFormState.ward,
        value: '',
        id: '',
      },
    }));
    current.setRoleFormData((roleFormData) => ({
      ...roleFormData,
      ward: {
        ...roleFormData.ward,
        '#options': [],
      },
    }));
  }, [roleFormState?.ulb?.id]);

  useEffect(() => {
    const current = latestRef.current;
    const fetchVillageOptions = async () => {
      const blockSatisfied = current.hasBlocksForDistrict === false ? true : !!current.roleFormState.block?.id;
      if (
        blockSatisfied &&
        (current.roleFormData?.sub_district['#required'] == true ? current.roleFormState?.sub_district?.id : true)
      ) {
        current.isGrampanchayat
          ? current.getGramPanchayat(current.roleFormState.block.id)
          : current.getVillage(current.roleFormState.block.id);
        return;
      }

      current.setRoleFormData((role_details) => ({
        ...role_details,
        gram_panchayat: {
          ...role_details.gram_panchayat,
          '#options': [],
          '#required': false,
        },
        village: {
          ...role_details.village,
          '#options': [],
          '#required': false,
        },
      }));
      current.setRoleFormState((roleFormState) => ({
        ...roleFormState,
        gram_panchayat: {
          ...roleFormState.gram_panchayat,
          value: '',
          id: '',
        },
        village: {
          ...roleFormState.village,
          value: '',
          id: '',
        },
      }));
    };

    fetchVillageOptions();
  }, [roleFormState?.block?.id, roleFormState?.sub_district?.id, hasBlocksForDistrict, roleFormData?.sub_district?.['#required']]);

  useEffect(() => {
    const current = latestRef.current;
    current.setRoleFormState((roleFormState) => ({
      ...roleFormState,
      ulb: {
        ...roleFormState?.ulb,
        value: '',
        id: '',
      },
      ward: {
        ...roleFormState?.ward,
        value: '',
        id: '',
      },
      block: {
        ...roleFormState?.block,
        value: '',
        id: '',
      },
      village: {
        ...roleFormState?.village,
        value: '',
        id: '',
      },
      gram_panchayat: {
        ...roleFormState?.gram_panchayat,
        value: '',
        id: '',
      },
    }));
  }, [roleFormState?.rural_urban?.id]);

  useEffect(() => {
    const current = latestRef.current;
    if (!current.loggedInUserDetails) return;

    const userStateId = current.loggedInUserDetails?.state_id;
    const userDistrictId = current.loggedInUserDetails?.district_id;
    const userSubDistrictId = current.loggedInUserDetails?.sub_district_id;
    const userBlockId = current.loggedInUserDetails?.block_id;
    const userVillageId = current.loggedInUserDetails?.village_id;
    const userWardId = current.loggedInUserDetails?.ward_id;
    const userUlbId = current.loggedInUserDetails?.ulb_id;

    if (!userStateId || !userDistrictId) return;
    if (String(current.roleFormState?.state?.id || '') !== String(userStateId)) return;
    if (String(current.roleFormState?.district?.id || '') !== String(userDistrictId)) return;

    const subDistrictOptions = current.roleFormData?.sub_district?.['#options'] || [];
    const blockOptions = current.roleFormData?.block?.['#options'] || [];
    const villageOptions = current.roleFormData?.village?.['#options'] || [];
    const wardOptions = current.roleFormData?.ward?.['#options'] || [];
    const ulbOptions = current.roleFormData?.ulb?.['#options'] || [];

    const shouldAutoSelectSubDistrict =
      shouldLockLoggedInHierarchyField(current.userTypeName, 'sub_district', current.loggedInUserDetails) ||
      (!!userSubDistrictId && !current.roleFormState.sub_district?.value && subDistrictOptions.length);

    if (
      shouldAutoSelectSubDistrict &&
      userSubDistrictId &&
      subDistrictOptions.length &&
      (!current.roleFormState.sub_district?.value || String(current.roleFormState.sub_district.id || '') !== String(userSubDistrictId))
    ) {
      const matchedSubDistrict = current.findMatchedOption(subDistrictOptions, userSubDistrictId);
      if (matchedSubDistrict) {
        current.setRoleFormState((prevState) => ({
          ...prevState,
          sub_district: {
            ...prevState.sub_district,
            value: matchedSubDistrict.value,
            id: String(matchedSubDistrict.id),
            code: matchedSubDistrict.code ?? prevState.sub_district?.code ?? '',
          },
        }));
      }
    }

    const shouldAutoSelectBlock =
      shouldLockLoggedInHierarchyField(current.userTypeName, 'block', current.loggedInUserDetails) ||
      (!!userBlockId && !current.roleFormState.block?.value && blockOptions.length);

    if (
      shouldAutoSelectBlock &&
      userBlockId &&
      blockOptions.length &&
      (!current.roleFormState.block?.value || String(current.roleFormState.block.id || '') !== String(userBlockId))
    ) {
      const matchedBlock = current.findMatchedOption(blockOptions, userBlockId);
      if (matchedBlock) {
        current.setRoleFormState((prevState) => ({
          ...prevState,
          block: {
            ...prevState.block,
            value: matchedBlock.value,
            id: String(matchedBlock.id),
            code: matchedBlock.code ?? prevState.block?.code ?? '',
          },
        }));
      }
    }

    if (
      shouldLockLoggedInHierarchyField(current.userTypeName, 'village', current.loggedInUserDetails) &&
      userVillageId &&
      villageOptions.length &&
      (!current.roleFormState.village?.value || String(current.roleFormState.village.id || '') !== String(userVillageId))
    ) {
      const matchedVillage = current.findMatchedOption(villageOptions, userVillageId);
      if (matchedVillage) {
        current.setRoleFormState((prevState) => ({
          ...prevState,
          village: {
            ...prevState.village,
            value: matchedVillage.value,
            id: String(matchedVillage.id),
            code: matchedVillage.code ?? prevState.village?.code ?? '',
          },
        }));
      }
    }

    if (
      shouldLockLoggedInHierarchyField(current.userTypeName, 'ward', current.loggedInUserDetails) &&
      userWardId &&
      wardOptions.length &&
      (!current.roleFormState.ward?.value || String(current.roleFormState.ward.id || '') !== String(userWardId))
    ) {
      const matchedWard = current.findMatchedOption(wardOptions, userWardId);
      if (matchedWard) {
        current.setRoleFormState((prevState) => ({
          ...prevState,
          ward: {
            ...prevState.ward,
            value: matchedWard.value,
            id: String(matchedWard.id),
            code: matchedWard.code ?? prevState.ward?.code ?? '',
          },
        }));
      }
    }

    const shouldAutoSelectUlb =
      shouldLockLoggedInHierarchyField(current.userTypeName, 'ulb', current.loggedInUserDetails) ||
      (!!userUlbId && !current.roleFormState.ulb?.value && ulbOptions.length);

    if (
      shouldAutoSelectUlb &&
      userUlbId &&
      ulbOptions.length &&
      (!current.roleFormState.ulb?.value || String(current.roleFormState.ulb.id || '') !== String(userUlbId))
    ) {
      const matchedUlb = current.findMatchedOption(ulbOptions, userUlbId);
      if (matchedUlb) {
        current.setRoleFormState((prevState) => ({
          ...prevState,
          ulb: {
            ...prevState.ulb,
            value: matchedUlb.value,
            id: String(matchedUlb.id),
            code: matchedUlb.code ?? prevState.ulb?.code ?? '',
          },
        }));
      }
    }
  }, [
    loggedInUserDetails,
    roleFormState?.state?.id,
    roleFormState?.district?.id,
    roleFormState?.block?.value,
    roleFormState?.block?.id,
    roleFormState?.village?.value,
    roleFormState?.village?.id,
    roleFormState?.ward?.value,
    roleFormState?.ward?.id,
    roleFormState?.ulb?.value,
    roleFormState?.ulb?.id,
    roleFormData?.block?.['#options'],
    roleFormData?.village?.['#options'],
    roleFormData?.ward?.['#options'],
    roleFormData?.ulb?.['#options'],
    hasBlocksForDistrict,
  ]);
};

export const useFacilityLocationEffects = (params) => {
  const latestRef = useRef(params);
  const { roleFormState } = params;

  useEffect(() => {
    latestRef.current = params;
  }, [params]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.roleFormState?.village?.id) {
      current.isLoggedInUserANM()
        ? current.getFacilityByLoggedInUser()
        : current.getFacilityByVillage(current.roleFormState?.village?.id);
      current.setFacilityLocationId(current.roleFormState.village.id);
      current.setFacilityLocationType(current.locationTypes.Village);
    }
  }, [roleFormState?.village?.id]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.roleFormState?.gram_panchayat?.id) {
      current.isLoggedInUserANM()
        ? current.getFacilityByLoggedInUser()
        : current.getFacilityByGramPanchayat(current.roleFormState?.gram_panchayat?.id);
      current.setFacilityLocationId(current.roleFormState.gram_panchayat.id);
      current.setFacilityLocationType(current.locationTypes.GP);
    }
  }, [roleFormState?.gram_panchayat?.id]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.roleFormState?.ward?.id) {
      current.isLoggedInUserANM()
        ? current.getFacilityByLoggedInUser()
        : current.getFacilityByWard(current.roleFormState.ward.id);
      current.setFacilityLocationId(current.roleFormState.ward.id);
      current.setFacilityLocationType(current.locationTypes.Ward);
    }
  }, [roleFormState?.ward?.id]);
};

export const useTaggedProviderEffects = (params) => {
  const latestRef = useRef(params);
  const { isFacilityLevelUser, isFieldLevelUser, facilityId, fieldFacilityId } = params;

  useEffect(() => {
    latestRef.current = params;
  }, [params]);

  useEffect(() => {
    const current = latestRef.current;
    if (current.isFacilityLevelUser && current.facilityId) {
      current.getASHAMobiliserTagged(current.facilityId);
    }

    if (current.isFieldLevelUser && current.fieldFacilityId) {
      current.getHealthProviderTagged(current.fieldFacilityId);
    }
  }, [isFacilityLevelUser, isFieldLevelUser, facilityId, fieldFacilityId]);
};
